"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { CustomBreadcrumbs } from "ui"
import Container from "ui/Container"
import { extractProperty, getBreadCrumbs, replaceRichTextLinks } from "utils"
import ReactPlayerComponent from "../Homepage/Hero/ReactPlayer"
import { formatDate } from "../PeopleOfEgged/ArticlesGrid"

const StyledLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  gap: 16px;
  padding-bottom: 64px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    padding-top: 48px;
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledTitle = styled(Typography)`
  font-size: 32px;
  line-height: 106.25%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 64px;
    line-height: 87.5%;
  }
`

const DescriptionText = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DetailsBox = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-top: 1px solid #f5f6f8;
  border-bottom: 1px solid #f5f6f8;
`

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const MediaContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`
interface ArticleContentItem {
  media: string
  content: string
  subTitle: string
}

export default function ContentPage({
  data,
  locale,
}: {
  data: any
  locale: string
}) {
  const page = data?.page
  const articleTitle = extractProperty(page, "articleTitle")
  const articleShortDescription = extractProperty(
    page,
    "articleShortDescription",
  )
  const articleDate = extractProperty(page, "articleDate")
  const articleAuthor = extractProperty(page, "articleAuthor")
  const articleContent = extractProperty(page, "articleContent") || []
  const items = articleContent.map((item: any) => {
    return {
      media: extractProperty(item, "media")?.[0]?.url,
      content: extractProperty(item, "content"),
      subTitle: extractProperty(item, "subTitle"),
    } as ArticleContentItem
  })
  const breadCrumbs = getBreadCrumbs(data, locale)
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, "")
  }
  return (
    <Container paddingMobile={true}>
      <StyledLayout>
        <CustomBreadcrumbs breadcrumbs={breadCrumbs} noPadding />
        <ContentLayout>
          <TextBox>
            <StyledTitle variant="h1">{articleTitle}</StyledTitle>
            <DescriptionText variant="body1">
              {articleShortDescription}
            </DescriptionText>
          </TextBox>
          <DetailsBox>
            <Typography variant="body2">
              עודכן בתאריך {formatDate(articleDate)}
            </Typography>
          </DetailsBox>

          {items.map((item: ArticleContentItem, index: number) => (
            <ContentBox key={index}>
              <MediaContainer>
                <ReactPlayerComponent
                  source={item.media}
                  playing={false}
                  volume={false}
                  setPlaying={() => {}}
                  setVolume={() => {}}
                />
              </MediaContainer>
              <Typography variant="h2">{item.subTitle}</Typography>
              <StyledTypography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(item.content),
                }}
              />
            </ContentBox>
          ))}
        </ContentLayout>
      </StyledLayout>
    </Container>
  )
}
